
import React, { useState, useMemo } from 'react';
import { Solicitacao } from '@/types/solicitacao';
import { useRegistrosRelacionados } from '@/hooks/useRegistrosRelacionados';
import { useSolicitacoes } from '@/hooks/useSolicitacoes';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Trash2, Plus, Link2, Search, ExternalLink } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { EditRequisicaoDialog } from './EditRequisicaoDialog';

interface Props {
  requisicao: Solicitacao;
}

const RelacionamentoSection = ({
  title,
  description,
  items,
  onRemove,
  onAdd,
  onOpenRelacionada,
  solicitacoes,
  currentId,
  existingIds,
  isPending,
}: {
  title: string;
  description: string;
  items: any[];
  onRemove: (id: string, otherSolicitacaoId: string) => void;
  onAdd: (relacionadaId: string) => void;
  onOpenRelacionada: (relacionadaId: string) => void;
  solicitacoes: Solicitacao[];
  currentId: string;
  existingIds: string[];
  isPending: boolean;
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [open, setOpen] = useState(false);

  const availableSolicitacoes = useMemo(() => {
    return solicitacoes.filter(
      s => s.id !== currentId && !existingIds.includes(s.id)
    );
  }, [solicitacoes, currentId, existingIds]);

  const filteredSolicitacoes = useMemo(() => {
    if (!searchTerm.trim()) return availableSolicitacoes;
    const term = searchTerm.toLowerCase();
    return availableSolicitacoes.filter(
      s => s.numero.toLowerCase().includes(term) || s.titulo.toLowerCase().includes(term)
    );
  }, [availableSolicitacoes, searchTerm]);

  const handleSelect = (id: string) => {
    onAdd(id);
    setSearchTerm('');
    setOpen(false);
  };

  return (
    <div className="space-y-3">
      <div>
        <h4 className="font-medium text-sm">{title}</h4>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>

      <div className="flex gap-2">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por número ou título..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  if (!open) setOpen(true);
                }}
                onFocus={() => setOpen(true)}
                className="pl-9"
              />
            </div>
          </PopoverTrigger>
          <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start" onOpenAutoFocus={(e) => e.preventDefault()}>
            <ScrollArea className="max-h-[200px]">
              {filteredSolicitacoes.length > 0 ? (
                filteredSolicitacoes.map(s => (
                  <button
                    key={s.id}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground transition-colors cursor-pointer"
                    onClick={() => handleSelect(s.id)}
                    disabled={isPending}
                  >
                    <span className="font-mono text-xs text-muted-foreground mr-2">{s.numero}</span>
                    <span>{s.titulo}</span>
                  </button>
                ))
              ) : (
                <div className="p-3 text-sm text-muted-foreground text-center">
                  Nenhuma solicitação encontrada
                </div>
              )}
            </ScrollArea>
          </PopoverContent>
        </Popover>
      </div>

      {items.length > 0 ? (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Número</TableHead>
              <TableHead>Título</TableHead>
              <TableHead>SLA</TableHead>
              <TableHead>Atendente</TableHead>
              <TableHead className="w-[50px]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map(item => (
                <TableRow key={item.id} className="cursor-pointer hover:bg-muted/50" onClick={() => onOpenRelacionada(item.display_solicitacao_id)}>
                <TableCell className="font-mono text-xs text-primary hover:underline">{item.numero}</TableCell>
                <TableCell className="max-w-[200px] truncate">{item.titulo}</TableCell>
                <TableCell>{item.sla_nome || '-'}</TableCell>
                <TableCell>{item.atendente_nome || '-'}</TableCell>
                <TableCell>
                   <Button
                     variant="ghost"
                     size="icon"
                     onClick={(e) => { e.stopPropagation(); onRemove(item.id, item.display_solicitacao_id); }}
                     disabled={isPending}
                   >
                     <Trash2 className="h-4 w-4 text-destructive" />
                   </Button>
                 </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <div className="text-sm text-muted-foreground text-center py-4 border rounded-md">
          Nenhum registro relacionado
        </div>
      )}
    </div>
  );
};

export const RequisicaoRegistrosRelacionados = ({ requisicao }: Props) => {
  const { dependentes, dependeDe, isLoading, addRelacionamento, removeRelacionamento } = useRegistrosRelacionados(requisicao.id);
  const { solicitacoes = [] } = useSolicitacoes();
  const [openRelacionada, setOpenRelacionada] = useState<Solicitacao | null>(null);

  const allExistingIds = [...dependentes, ...dependeDe].map(r => r.display_solicitacao_id);

  const handleOpenRelacionada = (relacionadaId: string) => {
    const sol = solicitacoes.find(s => s.id === relacionadaId);
    if (sol) setOpenRelacionada(sol);
  };

  if (isLoading) {
    return <div className="flex justify-center py-8 text-muted-foreground">Carregando...</div>;
  }

  return (
    <div className="space-y-6 py-4">
      <div className="flex items-center gap-2 mb-2">
        <Link2 className="h-5 w-5 text-primary" />
        <h3 className="font-semibold">Registros Relacionados</h3>
      </div>

      <RelacionamentoSection
        title="Depende desta solicitação"
        description="Outras solicitações que dependem desta"
        items={dependentes}
        onRemove={(id, otherSolicitacaoId) => removeRelacionamento.mutate({ id, otherSolicitacaoId })}
        onAdd={(relacionadaId) => addRelacionamento.mutate({ relacionadaId, tipoRelacao: 'dependente' })}
        onOpenRelacionada={handleOpenRelacionada}
        solicitacoes={solicitacoes}
        currentId={requisicao.id}
        existingIds={allExistingIds}
        isPending={addRelacionamento.isPending || removeRelacionamento.isPending}
      />

      <RelacionamentoSection
        title="Esta solicitação depende de"
        description="Solicitações das quais esta depende"
        items={dependeDe}
        onRemove={(id, otherSolicitacaoId) => removeRelacionamento.mutate({ id, otherSolicitacaoId })}
        onAdd={(relacionadaId) => addRelacionamento.mutate({ relacionadaId, tipoRelacao: 'depende_de' })}
        onOpenRelacionada={handleOpenRelacionada}
        solicitacoes={solicitacoes}
        currentId={requisicao.id}
        existingIds={allExistingIds}
        isPending={addRelacionamento.isPending || removeRelacionamento.isPending}
      />

      {openRelacionada && (
        <EditRequisicaoDialog
          requisicao={openRelacionada}
          isOpen={!!openRelacionada}
          onClose={() => setOpenRelacionada(null)}
        />
      )}
    </div>
  );
};
